import 'bootstrap/dist/css/bootstrap.min.css';

import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { Form, Spin, Radio, Alert, Button, Checkbox, Select, Modal, Input, Tag, } from 'antd';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';

import StyleDropdowns from '../Components/Common/StyleDropdowns';
import { getParentProject, createNewVersion, getProject } from '../services/projects';
import { takeoffUrl, designToolUrl } from '../globalSettings';
import { DEFAULT_STYLE_INFO_VALUES, PROJECT_SCOPES } from 'src/constants/projectConstants';

import type { Project, StyleInfo, CopyAction, CreateNewVersionPayload, UnitType, UnitNumber } from '../types/project';
import { useAuth } from 'src/AuthContext';

const CreateNewVersion: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { user } = useAuth();

  const { parentProjectId } = useParams();

  // Get params
  const projectId = searchParams.get('projectId');
  const copyAction = searchParams.get('copyAction') as CopyAction | null;

  // State
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [projectToBaseOff, setProjectToBaseOff] = useState<Project | null>(null);
  const [baseVersion, setBaseVersion] = useState<Project | null>(null);
  const [originalStyleInfo, setOriginalStyleInfo] = useState<StyleInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [userHasMadeChanges, setUserHasMadeChanges] = useState(false);
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [showVersionChangeModal, setShowVersionChangeModal] = useState(false);
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);
  const [isEditingStyle, setIsEditingStyle] = useState(false);

  // NEW: post-measure unit selection state
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [unitNumbersByType, setUnitNumbersByType] = useState<Record<string, UnitNumber[]>>({});
  const [selectedUnitTypeId, setSelectedUnitTypeId] = useState<string | null>(null);
  const [selectedUnitsByType, setSelectedUnitsByType] = useState<Record<string, string[]>>({});
  const [customUnitNamesByType, setCustomUnitNamesByType] = useState<Record<string, string[]>>({});

  const projectScopeValue = Form.useWatch('projectScope', form);
  const postMeasureValue = Form.useWatch('postMeasure', form);

  const customerNameLabel = user.isMultifamily ? 'Developer' : 'Customer Name';

  // Validate params
  const hasValidParams = projectId && (copyAction === 'blueprint' || copyAction === 'full');

  // Fetch project details
  useEffect(() => {
    if (!projectId) return;

    setLoading(true);
    getParentProject(parentProjectId!)
      .then((data) => {
        setProjects(data);
        setBaseVersion(data.find((p) => p.id === projectId) || null);
      })
      .catch((err) => {
        toast.error('Failed to load project details');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [parentProjectId, projectId, form]);

  useEffect(() => {
    if (!baseVersion) return;

    getProject(baseVersion.id!)
      .then((data) => {
        setProjectToBaseOff(data);
      })
      .catch((err) => {
        toast.error('Failed to load project details');
        console.error(err);
      });
  }, [baseVersion]);

  // Helper: build unit type -> unit numbers index from base project
  const buildUnitIndexes = (project: Project | null) => {
    const types = (project?.unitTypes ?? []).filter((t) => !!t.id || !!t.name);
    const byType: Record<string, UnitNumber[]> = {};

    types.forEach((t) => {
      const typeId = String(t.id ?? t.name);
      byType[typeId] = (t.unitNumbers ?? []).filter((u) => !!u.id);
    });

    return { types, byType };
  };
  const buildUnitSelectionObjects = () => {
    const result: Array<{ name: string; unitTypeId: string }> = [];
  
    // existing selected IDs
    Object.entries(selectedUnitsByType).forEach(([typeId, unitIds]) => {
      const unitsForType = unitNumbersByType[typeId] ?? [];
  
      unitIds.forEach((unitId) => {
        const unit = unitsForType.find((u) => String(u.id) === unitId);
        if (!unit?.name) return;
  
        result.push({ name: unit.name, unitTypeId: String(typeId) });
      });
    });
  
    // custom typed names
    Object.entries(customUnitNamesByType).forEach(([typeId, names]) => {
      names.forEach((name) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        result.push({ name: trimmed, unitTypeId: String(typeId) });
      });
    });
  
    return result;
  };
  

  useEffect(() => {
    if (!baseVersion) return;
    setOriginalStyleInfo(baseVersion.styleInfo || null);

    // Set initial form values
    const isCustomScope = baseVersion.projectScope?.toLowerCase() === 'custom';
    const customerNameValue = baseVersion.customerName ?? baseVersion.customer ?? '';
    const formValues = {
      ...(baseVersion.styleInfo ?? DEFAULT_STYLE_INFO_VALUES),
      // Ensure boolean fields have explicit values
      includeLegs: baseVersion.styleInfo?.includeLegs ?? false,
      includeShelf: baseVersion.styleInfo?.includeShelf ?? false,
      includeTopDoors: baseVersion.styleInfo?.includeTopDoors ?? false,
      includeBottomDoors: baseVersion.styleInfo?.includeBottomDoors ?? false,

      projectName: baseVersion.name ?? '',
      projectScope: baseVersion.projectScope ?? 'AllClosets',
      customProjectScope: isCustomScope ? baseVersion.projectScopeCustom ?? '' : undefined,
      customerName: customerNameValue,

      modulesSwapAction: 'none',
      closetsToKeep: 'all',
      postMeasure: baseVersion.postMeasure ?? false,
      productLine: baseVersion.productLine ?? 'vista',
    };

    form.setFieldsValue(formValues);

    // Reset user changes flag when project changes
    setUserHasMadeChanges(false);
  }, [baseVersion, form]);

  useEffect(() => {
    const { types, byType } = buildUnitIndexes(projectToBaseOff);
    setUnitTypes(types);
    setUnitNumbersByType(byType);
    setSelectedUnitsByType({});
    setSelectedUnitTypeId(null);
    setCustomUnitNamesByType({});
  }, [projectToBaseOff]);
  // NEW: if postMeasure is turned off, clear selections
  useEffect(() => {
    if (!postMeasureValue) {
      setSelectedUnitsByType({});
      setSelectedUnitTypeId(null);
      setCustomUnitNamesByType({});
    }
  }, [postMeasureValue]);

  // Check if style info has changed
  const hasStyleChanges = (currentValues: any): boolean => {
    if (!originalStyleInfo) return false;

    const styleFields = [
      'color',
      'handleStyle',
      'handleColor',
      'rodColor',
      'drawerStyle',
      'doorStyle',
      'drawerColor',
      'doorColor',
      'includeLegs',
      'includeShelf',
      'includeTopDoors',
      'includeBottomDoors',
    ];

    return styleFields.some(
      (field) => currentValues[field] !== originalStyleInfo[field as keyof StyleInfo],
    );
  };

  // Handle version change
  const handleVersionChange = (newProjectId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('projectId', newProjectId);

    if (userHasMadeChanges) {
      setPendingVersionId(newProjectId);
      setShowVersionChangeModal(true);
    } else {
      navigate(url.pathname + url.search);
      performVersionChange(newProjectId);
    }
  };

  // Perform the actual version change
  const performVersionChange = (newProjectId: string) => {
    setIsChangingVersion(true);
    const newProject = projects?.find((p) => p.id === newProjectId) || null;
    setBaseVersion(newProject);
    // Update original style info for the new project
    setOriginalStyleInfo(newProject?.styleInfo || null);
    // Reset style editing state when changing versions
    setIsEditingStyle(false);
    // Reset after a brief delay to allow form to update
    setTimeout(() => {
      setIsChangingVersion(false);
    }, 100);
  };

  // Handle modal confirmation
  const handleModalOk = () => {
    if (pendingVersionId) {
      const url = new URL(window.location.href);
      url.searchParams.set('projectId', pendingVersionId);
      navigate(url.pathname + url.search);
      performVersionChange(pendingVersionId);
    }
    setShowVersionChangeModal(false);
    setPendingVersionId(null);
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setShowVersionChangeModal(false);
    setPendingVersionId(null);
  };

  // Track form field changes
  const handleFormChange = (changedFields: any) => {
    // Don't track changes during version switching
    if (isChangingVersion) return;

    // Track changes to any form field
    const trackedFields = [
      'projectName',
      'customerName',
      'projectScope',
      'customProjectScope',
      // Style fields
      'productLine',
      'color',
      'handleStyle',
      'handleColor',
      'rodColor',
      'drawerStyle',
      'doorStyle',
      'drawerColor',
      'doorColor',
      'includeLegs',
      'includeShelf',
      'includeTopDoors',
      'includeBottomDoors',
      // Version option fields
      'modulesSwapAction',
      'closetsToKeep',
      'postMeasure',
      'newVersionName',
    ];

    const hasTrackedFieldChange = changedFields.some((field: any) =>
      trackedFields.includes(field.name[0]),
    );

    if (hasTrackedFieldChange && !userHasMadeChanges) {
      setUserHasMadeChanges(true);
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (!projectId || !copyAction) return;

    setSubmitting(true);

    try {
      // Build payload based on copy action
      let payload: CreateNewVersionPayload;

      // Extract style info
      const styleInfo: StyleInfo = {
        color: values.color,
        handleStyle: values.handleStyle,
        handleColor: values.handleColor,
        rodColor: values.rodColor,
        drawerStyle: values.drawerStyle,
        doorStyle: values.doorStyle,
        drawerColor: values.drawerColor,
        doorColor: values.doorColor,
        includeLegs: values.includeLegs,
        includeShelf: values.includeShelf,
        includeTopDoors: values.includeTopDoors,
        includeBottomDoors: values.includeBottomDoors,
      };

      // Include styleInfo if:
      // 1. For design projects: only when user is editing style (isEditingStyle is true)
      // 2. For non-design projects: when there are style changes
      const shouldIncludeStyleInfo =
      baseVersion?.projectType?.toLowerCase() === 'design'
          ? isEditingStyle
          : hasStyleChanges(values);

      const trimmedProjectName = values.projectName?.trim();
      const trimmedCustomerName = values.customerName?.trim();
      const isCustomScopeSelection = values.projectScope?.toLowerCase() === 'custom';
      const detailsPayload = {
        ...(trimmedProjectName ? { name: trimmedProjectName } : {}),
        ...(trimmedCustomerName ? { customerName: trimmedCustomerName } : {}),
        ...(values.projectScope ? { projectScope: values.projectScope } : {}),
        ...(isCustomScopeSelection && values.customProjectScope
          ? { projectScopeCustom: values.customProjectScope }
          : {}),
      };

      const isPostMeasureSelected = !!values.postMeasure;

      // NEW: only when post measure true -> collect unitNumber IDs, require at least one
      let unitNumbersToInclude: Array<{name: string;  unitTypeId: string;}> = []
      if (copyAction === 'full' && isPostMeasureSelected) {
        unitNumbersToInclude = buildUnitSelectionObjects();

        if (unitNumbersToInclude.length === 0) {
          toast.error('Please select or add at least one unit number to include.');
          setSubmitting(false);
          return;
        }
      }

      if (copyAction === 'blueprint') {
        payload = {
          copyAction: 'blueprint',
          productLine: values.productLine,
          newVersionName: values.newVersionName,
          ...detailsPayload,
          ...(shouldIncludeStyleInfo && { styleInfo }),
        };
      } else {
        payload = {
          copyAction: 'full',
          productLine: values.productLine,
          ...detailsPayload,
          ...(shouldIncludeStyleInfo && { styleInfo }),
          modulesSwapAction: values.modulesSwapAction,
          closetsToKeep: values.closetsToKeep,
          postMeasure: isPostMeasureSelected,
          newVersionName: values.newVersionName,
          ...(isPostMeasureSelected && { unitNumbersToInclude }),
        };
      }

      const result = await createNewVersion(projectId, payload);
      toast.success('New version created successfully');

      // Redirect based on copy action
      if (copyAction === 'blueprint') {
        // Redirect to takeoff app
        window.location.href = `${takeoffUrl}/?project_id=${result.projectId}`;
      } else {
        // Redirect to project view page
        window.location.href = `${designToolUrl}/?project_id=${result.projectId}`;
      }
    } catch (error) {
      toast.error('Failed to create new version');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Show error if missing params
  if (!hasValidParams) {
    return (
      <div className='container py-4'>
        <Alert
          type='error'
          message='Invalid Parameters'
          description='Missing required parameters: projectId and copyAction (blueprint or full)'
        />
      </div>
    );
  }

  // Show loading
  if (loading) {
    return (
      <div className='container py-4 text-center'>
        <Spin size='large' />
      </div>
    );
  }

  // Show error if project not found
  // Show error if project not found
  if (!baseVersion && !loading) {
    return (
      <div className='container py-4'>
        <Alert type='error' message='Project not found' />
      </div>
    );
  }

  return (
    <div className='container py-4'>
      <div className='row'>
        <div className='col-md-8'>
          {baseVersion && (
            <div className='mb-4'>
              <h5>
                <strong>
                  Create new version ({copyAction === 'blueprint' ? 'Blueprint Copy' : 'Full Copy'})
                  of {baseVersion.name}
                </strong>
              </h5>
            </div>
          )}
        </div>
        <div className='col-md-4'>
          {projects && projects.length > 0 && (
            <div className='mb-4'>
              <label className='form-label small'>Version to Base Off:</label>
              <Select
                size='small'
                style={{ width: '100%' }}
                value={baseVersion?.id}
                onChange={handleVersionChange}
                placeholder='Select a version'
              >
                {projects.map((project) => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.versionName} {project.id === projectId && '(Current)'}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onFieldsChange={handleFormChange}
        initialValues={{
          modulesSwapAction: 'none',
          closetsToKeep: 'all',
          postMeasure: false,
          projectScope: 'AllClosets',
          projectName: '',
          customerName: '',
        }}
      >
        <Form.Item
          name='newVersionName'
          label='Version Name'
          rules={[
            { required: true, message: 'Please enter a version name' },
            { pattern: /^[^/]*$/, message: 'Version name cannot contain "/"' }
          ]}
        >
          <Input placeholder='Enter version name' />
        </Form.Item>

        <Form.Item
          name='projectName'
          label='Project Name'
          rules={[{ required: true, message: 'Please enter a project name' }]}
        >
          <Input placeholder='Enter project name' />
        </Form.Item>

        <Form.Item name='customerName' label={customerNameLabel}>
          <Input placeholder='Enter name' />
        </Form.Item>

        <Form.Item
          name='projectScope'
          label='Project Scope'
          rules={[{ required: true, message: 'Please select a project scope' }]}
        >
          <Select placeholder='Select project scope'>
            {PROJECT_SCOPES.map((scope) => (
              <Select.Option key={scope.value} value={scope.value}>
                {scope.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {projectScopeValue?.toLowerCase() === 'custom' && (
          <Form.Item
            name='customProjectScope'
            label='Custom Project Scope'
            rules={[{ required: true, message: 'Please enter a custom project scope' }]}
          >
            <Input placeholder='Enter custom project scope' />
          </Form.Item>
        )}

        {baseVersion?.projectType?.toLowerCase() === 'design' ? (
          <>
            {!isEditingStyle ? (
              <Button
                type='default'
                onClick={() => setIsEditingStyle(true)}
                style={{ marginBottom: '16px' }}
              >
                Edit Styling
              </Button>
            ) : (
              <>
                <Button
                  type='default'
                  onClick={() => setIsEditingStyle(false)}
                  style={{ marginBottom: '16px' }}
                >
                  Keep Original Styling
                </Button>
                <StyleDropdowns />
              </>
            )}
          </>
        ) : (
          <StyleDropdowns />
        )}

        {copyAction === 'full' && (
          <>
            {user.isMultifamily && (
              <>
                <h3 className='mt-4'>Version Options</h3>
                <div className='mt-4'>
                  <h6>Swap Closets</h6>
                  <Form.Item
                    name='modulesSwapAction'
                    rules={[{ required: true, message: 'Please select a swap option' }]}
                  >
                    <Radio.Group>
                      <Radio value='none'>None</Radio>
                      <Radio value='swapTowersWithTHDH'>VE - TH and DH?</Radio>
                      <Radio value='swapTowersWithMH'>VE - MH?</Radio>
                      <Radio value='addDrawersToTowers'>Drawer Add on?</Radio>
                    </Radio.Group>
                  </Form.Item>
                </div>

                <div className='mt-4'>
                  <h6>Restrict Scope</h6>
                  <Form.Item
                    name='closetsToKeep'
                    rules={[{ required: true, message: 'Please select a scope restriction' }]}
                  >
                    <Radio.Group>
                      <Radio value='all'>All</Radio>
                      <Radio value='masterClosets'>Master Closets</Radio>
                      <Radio value='bedroomClosets'>Bedroom Closets</Radio>
                    </Radio.Group>
                  </Form.Item>
                </div>
              </>
            )}

            {!baseVersion?.postMeasure && (
              <div className='mt-4'>
                <h6>Post Field Measure</h6>
                <Form.Item name='postMeasure' valuePropName='checked'>
                  <Checkbox>Yes</Checkbox>
                </Form.Item>
              </div>
            )}

            {/* NEW: Unit selection section only when postMeasure is true */}
            {postMeasureValue && (
              <div className='mt-4 p-3 border rounded'>
                <h5>Select Units to Include</h5>
                <p className='text-muted small'>
                  Choose a unit type, then select unit numbers to include in this version.
                </p>

                <Form.Item label='Unit Type'>
                  <Select
                    placeholder='Select a unit type'
                    value={selectedUnitTypeId ?? undefined}
                    onChange={(val) => setSelectedUnitTypeId(val)}
                    allowClear
                    showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                      }
                    options={unitTypes.map((t) => {
                      const id = String(t.id ?? t.name);
                      return { label: t.name ?? id, value: id };
                    })}
                  />
                </Form.Item>

                {selectedUnitTypeId && (
                  <Form.Item label='Unit Numbers'>
                    <div>
                    <Select
                      mode="tags"
                      onInputKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey) return;
                        const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Home', 'End'];
                        if (
                          !/[0-9]/.test(e.key) &&
                          !allowed.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="Select or type unit numbers"
                      style={{ width: '100%' }}
                      value={[
                        ...(selectedUnitsByType[selectedUnitTypeId] ?? []),
                        ...(customUnitNamesByType[selectedUnitTypeId] ?? []),
                      ]}
                      onChange={(vals) => {
                        const typeId = selectedUnitTypeId!;
                        const existingIdsSet = new Set(
                          (unitNumbersByType[typeId] ?? []).map(u => String(u.id))
                        );

                        const nextSelectedIds: string[] = [];
                        const nextCustomNames: string[] = [];

                        let hadInvalid = false;

                        (vals as string[]).forEach(v => {
                          const value = String(v).trim();

                          if (!/^\d+$/.test(value)) {
                            hadInvalid = true;
                            return;
                          }

                          if (existingIdsSet.has(value)) nextSelectedIds.push(value);
                          else nextCustomNames.push(value);
                        });

                        if (hadInvalid) {
                          toast.error('Unit numbers must be numeric only');
                        }

                        setSelectedUnitsByType(prev => ({ ...prev, [typeId]: nextSelectedIds }));
                        setCustomUnitNamesByType(prev => ({ ...prev, [typeId]: nextCustomNames }));
                      }}

                      options={(unitNumbersByType[selectedUnitTypeId] ?? []).map((u) => ({
                        label: u.name ?? u.id,
                        value: String(u.id), // IMPORTANT: store/send IDs
                      }))}
                    />
                  </div>
                  </Form.Item>
                )}

                {(Object.keys(selectedUnitsByType).length > 0 ||
                  Object.keys(customUnitNamesByType).length > 0) && (
                  <div className="mt-3">
                    <h6>Selected Units</h6>

                    {(() => {
                      const allUnits = Object.values(unitNumbersByType).flat();
                      const unitById = new Map(allUnits.map(u => [String(u.id), u]));

                      const combined = [
                        ...Object.entries(selectedUnitsByType).flatMap(([typeId, ids]) =>
                          ids.map((unitId) => {
                            const unit = unitById.get(unitId);
                            const typeName =
                              unitTypes.find((t) => String(t.id ?? t.name) === typeId)?.name ??
                              typeId;

                                return {
                                  kind: 'id' as const,
                                  key: `id:${typeId}:${unitId}`,
                                  unitId,
                                  typeId,
                                  display: `${unit?.name ?? unitId} - Type ${typeName.toUpperCase()}`,
                                  sortValue: Number(unit?.name ?? unitId),
                                };
                              }),
                            ),

                        ...Object.entries(customUnitNamesByType).flatMap(([typeId, names]) =>
                          names.map((name) => {
                            const typeName =
                              unitTypes.find((t) => String(t.id ?? t.name) === typeId)?.name ??
                              typeId;

                            return {
                              kind: 'custom' as const,
                              key: `custom:${typeId}:${name}`,
                              name,
                              typeId,
                              display: `${name} - Type ${typeName.toUpperCase()}`,
                              sortValue: Number(name),
                            };
                        })),
                      ];

                // Sort numericly
                combined.sort((a, b) => a.sortValue - b.sortValue);

  return (
    <div className="d-flex flex-wrap gap-2">
      {combined.map((item) => (
            <Tag
              key={item.key}
              closable
              onClose={() => {
                                if (item.kind === 'id') {
                setSelectedUnitsByType((prev) => ({
                                    ...prev,
                                    [item.typeId]: (prev[item.typeId] ?? []).filter(
                                      (id) => id !== item.unitId,
                                    ),
                                  }));
                                } else {
                                  setCustomUnitNamesByType((prev) => ({
                                    ...prev,
                                    [item.typeId]: (prev[item.typeId] ?? []).filter(
                                      (n) => n !== item.name,
                                    ),
                                  }));
                                }
                              }}
                            >
                              {item.display}
                            </Tag>
                          )
                  )}
                      </div>
                    );
                  })()}

                  </div>
                )}



                {unitTypes.length === 0 && (
                  <Alert
                    type='warning'
                    showIcon
                    message='No unit types found'
                    description='This version does not contain unit data to select from.'
                    style={{ marginTop: 12 }}
                  />
                )}
              </div>
            )}
          </>
        )}

        <Form.Item className='mt-4'>
          <Button type='primary' htmlType='submit' loading={submitting} block>
            Create
          </Button>
        </Form.Item>
      </Form>

      {/* Version Change Confirmation Modal */}
      <Modal
        title='Unsaved Changes'
        open={showVersionChangeModal}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText='OK'
        cancelText='Cancel'
      >
        <div>
          <p>Switching versions will reset all your changes</p>
          <p>Are you sure you want to continue?</p>
        </div>
      </Modal>
    </div>
  );
};

export default CreateNewVersion;
